import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  searchData,
  DataSearchResponseItem,
  DataSearchRequest
} from '../../services/endpoints/data'
import { RequestErrorResponse } from '../../services/lib'
import { formatError } from '../lib/errors'
import { renderDateTime } from '../../utils/dateFormatter'

const sliceName = 'data'

export interface DataStateVersionItem {
  type: string
  selected: boolean
  size: number
  startDate: string
  endDate: string
  fileName: string
  vehicleName: string
  deviceId: number
}

export interface DataStateVersions {
  [id: number]: DataStateVersionItem
}

export interface DataStateFileTypes {
  [id: string]: boolean
}
export type DataStatePaging = {
  max: number //max records per page
  offset: number //current number of records offset
  total: number //Total records for query
}
export interface DataState {
  fileTypes: DataStateFileTypes
  versions: DataStateVersions
  data: DataSearchResponseItem[] | null
  paging: DataStatePaging | null
  loading: boolean
  error: RequestErrorResponse | null
  lastRequest: DataSearchRequest | null
  groups: string[]
}

export type PartialSearchRequest =
DataSearchRequest |
{ sort: string, order: 'desc' | 'asc' } |
{ offset: number } | undefined

const _getSearchRequest = (
  req: PartialSearchRequest,
  lastReq: DataSearchRequest | null
): DataSearchRequest | null => {
  if(req === undefined) {
    return lastReq
  } else if(req['vehicleId'] !== undefined) {
    //hate this, but don't feel like writting a proper function to "confirm"
    return req as DataSearchRequest
  } else if(req['offset'] !== undefined) {
    return lastReq === null ? lastReq : {
      ...lastReq,
      offset: req['offset']
    }
  } else if (req['sort'] !== undefined && req['order'] !== undefined) {
    return lastReq === null ? lastReq : {
      ...lastReq,
      sort: req['sort'],
      order: req['order']
    }    
  } else {
    console.error('Error calculating next search request, request arg was invalid', req)
    return null
  }
}

//Handles partial searches using previous request
export const searchDataThunk = createAsyncThunk<
  { paging: DataStatePaging, data: DataSearchResponseItem[]; usedRequest: DataSearchRequest },
  PartialSearchRequest,
  { state: { data: DataState } }
>(`${sliceName}/search`, async (req, { rejectWithValue, getState }) => {
  const finalRequest = _getSearchRequest(req, getState().data.lastRequest)

  if (!finalRequest) {
    return rejectWithValue(new Error('No request data available'))
  }

  try {
    return { ...(await searchData(finalRequest)), usedRequest: finalRequest }
  } catch (err: any) {
    return rejectWithValue(err)
  }
})

const generateOtherData = (
  data: DataSearchResponseItem[] | null
): [DataStateFileTypes, DataStateVersions] => {
  const fileTypes: DataStateFileTypes = {}
  const versions: DataStateVersions = {}

  if (data) {
    data.forEach((u) => {
      u.versions.forEach((v) => {
        if (!(v.extension in fileTypes)) {
          fileTypes[v.extension] = false
        }

        versions[v.id] = {
          type: v.extension,
          size: v.size,
          selected: false,
          startDate: renderDateTime({ value: u.startDate }) || 'Unknown',
          endDate: renderDateTime({ value: u.endDate }) || 'Unknown',
          fileName: u.displayName,
          vehicleName: u.vehicle.name,
          deviceId: u.device.id
        }
      })
    })
  }

  return [fileTypes, versions]
}

export const initialDataState: DataState = {
  fileTypes: {},
  versions: {},
  data: [],
  loading: false,
  error: null,
  lastRequest: null,
  paging: null,
  groups: []
}

export const dataSlice = createSlice({
  name: sliceName,
  initialState: initialDataState,
  reducers: {
    toggleType: (state, action: PayloadAction<string>) => {
      const type = action.payload

      if (!(type in state.fileTypes)) {
        console.error(
          `Trying to toggle file type state for ${type} which doesn't exist`
        )
        return
      }

      const newState = !state.fileTypes[type]
      state.fileTypes[type] = newState

      if (state.versions === null) {
        console.error(
          'Trying to toggle file type state for all versions but versions do not exist'
        )
        return
      }

      Object.keys(state.versions).forEach((vid) => {
        if (state.versions && state.versions[vid].type == type) {
          state.versions[vid].selected = newState
        }
      })
    },
    toggleVersion: (state, action: PayloadAction<number>) => {
      const vid = action.payload

      if (!state.versions || !(vid in state.versions)) {
        console.error(
          `Trying to change version state for ${vid} which doesn't exist`
        )
        return
      }

      const newState = !state.versions[vid].selected

      if (!newState && state.fileTypes[state.versions[vid].type]) {
        state.fileTypes[state.versions[vid].type] = false
      }

      state.versions[vid].selected = newState
    },
    clearAllSelections: (state) => {
      Object.keys(state.fileTypes).forEach((extType) => {
        state.fileTypes[extType] = false
      })

      Object.keys(state.versions).forEach((vid) => {
        state.versions[vid].selected = false
      })
    }
  },
  extraReducers(builder) {
    builder
      .addCase(searchDataThunk.fulfilled, (state, action) => {
        const [types, versions] = generateOtherData(action.payload.data)
        state.groups = []
        const newData = action.payload.data.map(i => {
          if(state.groups.indexOf(i.groupId || '') === -1) {
            state.groups.push(i.groupId || '')
          }
          i.groupId = `${state.groups.indexOf(i.groupId || '')}`
          return i
        })
        state.lastRequest = action.payload.usedRequest
        state.fileTypes = types
        state.versions = versions
        state.data = newData
        state.error = null
        state.loading = false
        state.paging = action.payload.paging
      })
      .addCase(searchDataThunk.pending, (state, _action) => {
        state.fileTypes = {}
        state.versions = {}
        state.data = null
        state.error = null
        state.loading = true
        state.groups = []
      })
      .addCase(searchDataThunk.rejected, (state, action) => {
        state.fileTypes = {}
        state.versions = {}
        state.data = null
        state.paging = null
        state.error = action.payload ? formatError(action.payload) : null
        state.loading = false
        state.groups = []
      })
  }
})
